import React, { useEffect, useRef } from 'react';
import { ActiveCustomer } from '../../../types/organizations';

interface PointOfContactInputModalProps {
  isOpen: boolean;
  customer: ActiveCustomer | null;
  channel: 'email' | 'whatsapp';
  onChannelChange: (channel: 'email' | 'whatsapp') => void;
  showChannelSelection?: boolean; // Control whether to show channel selection UI
  picName: string;
  picPhoneNumber: string;
  picEmail: string;
  countryCode: string;
  conversationLoading: boolean;
  conversationError: string;
  useMultipleInvoicesTemplate?: boolean;
  existingConversations?: any[];
  selectedConversationId?: string | null;
  onSelectedConversationIdChange?: (id: string | null) => void;
  useExistingConversation: boolean;
  onClose: () => void;
  onPicNameChange: (name: string) => void;
  onPicPhoneNumberChange: (phone: string) => void;
  onPicEmailChange: (email: string) => void;
  onCountryCodeChange: (code: string) => void;
  onUseExistingConversationChange: (use: boolean) => void;
  onStartConversation: (useMultipleInvoicesTemplate?: boolean) => void;
}

const PointOfContactInputModal: React.FC<PointOfContactInputModalProps> = ({
  isOpen,
  customer,
  channel,
  onChannelChange,
  showChannelSelection = true, // Default to true for backward compatibility
  picName,
  picPhoneNumber,
  picEmail,
  countryCode,
  conversationLoading,
  conversationError,
  useMultipleInvoicesTemplate,
  existingConversations = [],
  selectedConversationId,
  onSelectedConversationIdChange,
  useExistingConversation,
  onClose,
  onPicNameChange,
  onPicPhoneNumberChange,
  onPicEmailChange,
  onCountryCodeChange,
  onUseExistingConversationChange,
  onStartConversation,
}) => {
  const hasExistingConversations = existingConversations.length > 0;
  const selectedConversation = selectedConversationId 
    ? existingConversations.find((conv: any) => conv.id === selectedConversationId)
    : null;
  
  // Track previous channel to detect actual channel changes
  const prevChannelRef = useRef<'email' | 'whatsapp' | null>(null);

  // When channel changes, try to find a conversation with that channel and select it
  // Only run this when channel actually changes, not when selectedConversationId changes
  useEffect(() => {
    if (!isOpen || !customer) return;
    
    // Only run auto-selection logic when channel actually changes
    const channelChanged = prevChannelRef.current !== null && prevChannelRef.current !== channel;
    prevChannelRef.current = channel;
    
    if (!channelChanged) return; // Don't run if channel hasn't changed
    
    if (hasExistingConversations && useExistingConversation && onSelectedConversationIdChange) {
      // Find conversations with the selected channel
      const conversationsWithChannel = existingConversations.filter((conv: any) => 
        channel === 'email' ? !!conv.pic_contact?.email : !!conv.pic_contact?.whatsapp
      );
      
      // Check if current selection has the required channel
      const currentSelectedConv = selectedConversationId 
        ? existingConversations.find((conv: any) => conv.id === selectedConversationId)
        : null;
      const hasRequiredChannel = currentSelectedConv 
        ? (channel === 'email' ? !!currentSelectedConv.pic_contact?.email : !!currentSelectedConv.pic_contact?.whatsapp)
        : false;
      
      // If current selection doesn't have the channel, try to find one that does
      if (!hasRequiredChannel && conversationsWithChannel.length > 0) {
        onSelectedConversationIdChange(conversationsWithChannel[0].id);
        // Update form fields
        const conv = conversationsWithChannel[0];
        if (conv.pic_name) {
          onPicNameChange(conv.pic_name);
        }
        if (channel === 'email' && conv.pic_contact?.email) {
          onPicEmailChange(conv.pic_contact.email);
        } else if (channel === 'whatsapp' && conv.pic_contact?.whatsapp) {
          const whatsappNum = conv.pic_contact.whatsapp;
          const match = whatsappNum.match(/^(\+\d{1,3})(.+)$/);
          if (match) {
            onCountryCodeChange(match[1]);
            onPicPhoneNumberChange(match[2]);
          } else {
            onPicPhoneNumberChange(whatsappNum);
          }
        }
      } else if (!hasRequiredChannel && conversationsWithChannel.length === 0 && existingConversations.length > 1) {
        // No conversation has this channel and there are multiple conversations, clear selection
        // (If there's only one conversation, keep it selected so user can add the channel to it)
        onSelectedConversationIdChange(null);
      }
      // If there's only one conversation and it doesn't have the channel, keep it selected
      // so the user can add the channel to it
    }
  }, [channel, isOpen, customer, hasExistingConversations, useExistingConversation, onSelectedConversationIdChange, existingConversations, selectedConversationId, onPicNameChange, onPicEmailChange, onPicPhoneNumberChange, onCountryCodeChange]);

  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {hasExistingConversations
              ? `Add ${channel === 'email' ? 'Email' : 'WhatsApp'} to Conversation`
              : channel === 'email' ? 'Start Email Conversation' : 'Start WhatsApp Conversation'
            }
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              {hasExistingConversations
                ? <>Add {channel === 'email' ? 'email' : 'WhatsApp'} to existing conversation with <strong>{customer.name}</strong></>
                : <>Start a {channel === 'email' ? 'email' : 'WhatsApp'} conversation with <strong>{customer.name}</strong></>
              }
            </p>
          </div>

          {/* Channel Selection - Only show if showChannelSelection is true */}
          {showChannelSelection && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conversation Channel
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="channel"
                    value="whatsapp"
                    checked={channel === 'whatsapp'}
                    onChange={() => onChannelChange('whatsapp')}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="text-sm text-gray-700">WhatsApp</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="channel"
                    value="email"
                    checked={channel === 'email'}
                    onChange={() => onChannelChange('email')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Email</span>
                </label>
              </div>
            </div>
          )}

          {hasExistingConversations && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Existing conversation(s) found:</strong> This customer already has {existingConversations.length} conversation record(s). 
                You can add {channel === 'email' ? 'an email address' : 'a WhatsApp number'} to an existing conversation or create a new one.
              </p>
            </div>
          )}

          {hasExistingConversations && (
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="radio"
                id="use-existing"
                checked={useExistingConversation}
                onChange={() => {
                  onUseExistingConversationChange(true);
                  // Restore pic_name from selected conversation when switching back
                  if (selectedConversation?.pic_name) {
                    onPicNameChange(selectedConversation.pic_name);
                  }
                }}
                className="w-4 h-4 text-blue-600"
                disabled={!selectedConversationId}
              />
              <label htmlFor="use-existing" className={`text-sm ${!selectedConversationId ? 'text-gray-400' : 'text-gray-700'}`}>
                Add {channel === 'email' ? 'email' : 'WhatsApp'} to existing conversation
              </label>
            </div>
          )}

          {hasExistingConversations && (
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="radio"
                id="create-new"
                checked={!useExistingConversation}
                onChange={() => {
                  onUseExistingConversationChange(false);
                  onPicNameChange(''); // Clear pic name when creating new conversation
                }}
                className="w-4 h-4 text-blue-600"
              />
              <label htmlFor="create-new" className="text-sm text-gray-700">
                Create new conversation
              </label>
            </div>
          )}

          {hasExistingConversations && existingConversations.length > 1 && useExistingConversation && onSelectedConversationIdChange && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Conversation
              </label>
              <div className="relative">
                <select
                  value={selectedConversationId || ''}
                  onChange={(e) => {
                    const convId = e.target.value || null;
                    onSelectedConversationIdChange(convId);
                    // Update form fields based on selected conversation
                    const selectedConv = existingConversations.find((conv: any) => conv.id === convId);
                    if (selectedConv) {
                      onPicNameChange(selectedConv.pic_name || customer.name);
                      if (channel === 'email') {
                        onPicEmailChange(selectedConv.pic_contact?.email || customer.email_address || '');
                      } else {
                        const whatsappNum = selectedConv.pic_contact?.whatsapp || '';
                        if (whatsappNum) {
                          // Extract country code and number
                          const match = whatsappNum.match(/^(\+\d{1,3})(.+)$/);
                          if (match) {
                            onCountryCodeChange(match[1]);
                            onPicPhoneNumberChange(match[2]);
                          } else {
                            onPicPhoneNumberChange(whatsappNum);
                          }
                        } else {
                          // Reset to default when no WhatsApp in selected conversation
                          onCountryCodeChange('+65');
                          onPicPhoneNumberChange('');
                        }
                      }
                    }
                  }}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                >
                  <option value="">-- Select a conversation --</option>
                  {existingConversations.map((conv: any) => {
                    const hasChannel = channel === 'email' 
                      ? !!conv.pic_contact?.email 
                      : !!conv.pic_contact?.whatsapp;
                    const channelLabel = channel === 'email' ? 'Email' : 'WhatsApp';
                    return (
                      <option key={conv.id} value={conv.id}>
                        {conv.pic_name || 'Unnamed'} - {Object.keys(conv.pic_contact || {}).join(', ')} 
                        {hasChannel ? ` (Has ${channelLabel})` : ''}
                        {conv.last_activity ? ` (Last: ${new Date(conv.last_activity).toLocaleDateString()})` : ''}
                      </option>
                    );
                  })}
                </select>
                {/* Custom dropdown arrow */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Point-of-Contact Name
            </label>
            <input
              type="text"
              value={picName}
              onChange={(e) => onPicNameChange(e.target.value)}
              disabled={hasExistingConversations && useExistingConversation}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                hasExistingConversations && useExistingConversation
                  ? 'bg-gray-100 cursor-not-allowed' 
                  : ''
              }`}
              placeholder="Micheal"
            />
            {hasExistingConversations && useExistingConversation && (
              <p className="text-xs text-gray-500 mt-1">Using selected conversation's point-of-contact name</p>
            )}
          </div>

          {channel === 'email' ? (
            <>
              {/* Show existing WhatsApp number if using existing conversation */}
              {hasExistingConversations && useExistingConversation && selectedConversation?.pic_contact?.whatsapp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number (existing)
                  </label>
                  <input
                    type="text"
                    value={selectedConversation.pic_contact.whatsapp}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Existing WhatsApp number from this conversation</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email to contact
                </label>
                {hasExistingConversations && useExistingConversation && selectedConversation?.pic_contact?.email ? (
                  <>
                    <input
                      type="text"
                      value={selectedConversation.pic_contact.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">Existing email from this conversation</p>
                  </>
                ) : (
                  <input
                    type="email"
                    value={picEmail}
                    onChange={(e) => onPicEmailChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="customer@example.com"
                  />
                )}
              </div>
            </>
          ) : (
            <>
              {/* Show existing email if using existing conversation */}
              {hasExistingConversations && useExistingConversation && selectedConversation?.pic_contact?.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email (existing)
                  </label>
                  <input
                    type="text"
                    value={selectedConversation.pic_contact.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Existing email from this conversation</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                {hasExistingConversations && useExistingConversation && selectedConversation?.pic_contact?.whatsapp ? (
                  <>
                    <input
                      type="text"
                      value={selectedConversation.pic_contact.whatsapp}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">Existing WhatsApp number from this conversation</p>
                  </>
                ) : (
                  <div className="flex space-x-2">
                    <div className="relative">
                      <select
                        value={countryCode}
                        onChange={(e) => onCountryCodeChange(e.target.value)}
                        className="px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white w-full"
                      >
                        <option value="+1">+1 (US)</option>
                        <option value="+44">+44 (UK)</option>
                        <option value="+61">+61 (AU)</option>
                        <option value="+65">+65 (SG)</option>
                        <option value="+60">+60 (MY)</option>
                        <option value="+66">+66 (TH)</option>
                        <option value="+84">+84 (VN)</option>
                        <option value="+86">+86 (CN)</option>
                        <option value="+81">+81 (JP)</option>
                        <option value="+82">+82 (KR)</option>
                      </select>
                      {/* Custom dropdown arrow */}
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <input
                      type="tel"
                      value={picPhoneNumber}
                      onChange={(e) => onPicPhoneNumberChange(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter phone number"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {conversationError && (
            <div className="text-red-600 text-sm">{conversationError}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onStartConversation(useMultipleInvoicesTemplate)}
              disabled={conversationLoading}
              className={`px-4 py-2 rounded-md transition-colors ${
                conversationLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : channel === 'email'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {conversationLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Starting...
                </div>
              ) : (
                'Proceed'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointOfContactInputModal;

